import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export const LA_SUB_REGIONS = {
  "la-westside": {
    name: "Westside Los Angeles",
    shortName: "Westside LA",
    description: "Premium home cleaning for Santa Monica, Venice, Brentwood, West LA & Culver City",
    cities: ["Santa Monica", "Venice", "Brentwood Los Angeles", "West LA", "Culver City"],
    highlights: ["Beach-adjacent homes", "Luxury condos & lofts", "Creative studios", "Upscale apartments"],
    testimonials: [
      { name: "Ashley R.", city: "Santa Monica", quote: "Uplevel turned my beach condo into an absolute showplace. Worth every penny!" },
      { name: "Marcus T.", city: "Venice", quote: "My creative studio has never been cleaner. The team is professional and thorough." },
      { name: "Jennifer L.", city: "Culver City", quote: "Fast booking, transparent pricing, and spotless results. I'm a customer for life." },
    ]
  },
  "la-san-fernando-valley": {
    name: "San Fernando Valley",
    shortName: "San Fernando Valley",
    description: "Expert house cleaning for Sherman Oaks, Encino, Studio City, Woodland Hills & Porter Ranch",
    cities: ["Sherman Oaks", "Encino", "Studio City", "Woodland Hills", "Porter Ranch"],
    highlights: ["Family homes & estates", "Entertainment industry professionals", "Gated communities", "Large multi-bedroom homes"],
    testimonials: [
      { name: "David K.", city: "Studio City", quote: "As a busy producer, I need a reliable cleaning team. Uplevel never lets me down." },
      { name: "Sandra M.", city: "Woodland Hills", quote: "They handle our 5-bedroom home perfectly every single visit. Highly recommend!" },
      { name: "Brian H.", city: "Encino", quote: "Transparent pricing and a team that shows up on time. Rare and refreshing." },
    ]
  },
  "la-san-gabriel-valley": {
    name: "San Gabriel Valley",
    shortName: "San Gabriel Valley",
    description: "Professional cleaning services for Pasadena, Arcadia, San Marino & Monrovia",
    cities: ["Pasadena", "Arcadia", "San Marino", "Monrovia"],
    highlights: ["Historic craftsman homes", "Luxury estates", "Academic & professional families", "Heritage properties"],
    testimonials: [
      { name: "Linda C.", city: "Pasadena", quote: "Our 1920s craftsman home requires special care. Uplevel treats it like their own." },
      { name: "Raymond W.", city: "San Marino", quote: "Top-tier service for a top-tier neighborhood. Absolutely worth it." },
      { name: "Priya N.", city: "Arcadia", quote: "Booking was effortless and the clean was impeccable. Will book again!" },
    ]
  },
  "la-south-bay": {
    name: "South Bay",
    shortName: "South Bay",
    description: "Top-rated home cleaning for Manhattan Beach, Redondo Beach, Torrance & El Segundo",
    cities: ["Manhattan Beach", "Redondo Beach", "Torrance", "El Segundo"],
    highlights: ["Beachfront properties", "Coastal homes & duplexes", "Tech & aerospace professionals", "Move-in/out specialists"],
    testimonials: [
      { name: "Tyler B.", city: "Manhattan Beach", quote: "Living near the beach means sand everywhere. Uplevel gets it all. Amazing team!" },
      { name: "Michelle S.", city: "Redondo Beach", quote: "Consistent, professional, and thorough. My go-to cleaning service in South Bay." },
      { name: "Jason A.", city: "El Segundo", quote: "Quick booking, great communication, and a spotless home. 5 stars all the way." },
    ]
  }
};

export type LASubRegionType = {
  slug: keyof typeof LA_SUB_REGIONS;
  name: string;
  shortName: string;
  description: string;
  cities: string[];
  highlights: string[];
  testimonials: { name: string; city: string; quote: string }[];
};

export const ALL_LA_SUB_REGIONS: LASubRegionType[] = Object.entries(LA_SUB_REGIONS).map(([slug, data]) => ({
  slug: slug as keyof typeof LA_SUB_REGIONS,
  ...data
}));

export const REGIONS = {
  "sacramento-area": {
    name: "Sacramento Area",
    cities: ["Sacramento", "Roseville", "Folsom", "Elk Grove", "Davis", "Rocklin", "Granite Bay", "El Dorado Hills", "West Sacramento", "Rancho Cordova", "Carmichael", "Citrus Heights", "Fair Oaks", "Gold River", "Antelope"]
  },
  "contra-costa": {
    name: "Contra Costa County",
    cities: ["Walnut Creek", "Concord", "Richmond", "Antioch", "San Ramon", "Danville", "Brentwood CA", "Martinez", "Pleasant Hill", "Lafayette"]
  },
  "solano": {
    name: "Solano County",
    cities: ["Vallejo", "Fairfield", "Vacaville", "Benicia", "Suisun City", "Dixon"]
  },
  "alameda": {
    name: "Alameda County",
    cities: ["Oakland", "Fremont", "Hayward", "Berkeley", "Pleasanton", "Livermore", "Alameda", "Dublin", "Union City", "Newark"]
  },
  "san-diego": {
    name: "San Diego Area",
    cities: ["San Diego", "Carlsbad", "Oceanside", "Chula Vista", "Escondido", "San Marcos", "Encinitas", "El Cajon", "Vista", "Coronado"]
  },
  "orange-county": {
    name: "Orange County",
    cities: ["Irvine", "Anaheim", "Santa Ana", "Huntington Beach", "Newport Beach", "Costa Mesa", "Mission Viejo", "Fullerton", "Orange", "San Clemente"]
  },
  "santa-clara": {
    name: "Santa Clara County",
    cities: ["San Jose", "Santa Clara", "Sunnyvale", "Mountain View", "Palo Alto", "Cupertino", "Milpitas", "Los Gatos", "Morgan Hill", "Gilroy"]
  },
  "marin": {
    name: "Marin County",
    cities: ["San Rafael", "Novato", "Mill Valley", "San Anselmo", "Corte Madera", "Tiburon", "Sausalito", "Larkspur", "Fairfax", "Belvedere"]
  },
  "san-francisco": {
    name: "San Francisco Area",
    cities: ["San Francisco", "Daly City", "South San Francisco", "San Bruno", "Pacifica", "Brisbane", "Colma", "Burlingame", "Millbrae", "San Mateo"]
  },
  "los-angeles": {
    name: "Los Angeles County",
    cities: ["Santa Monica", "Venice", "Brentwood Los Angeles", "West LA", "Culver City", "Sherman Oaks", "Encino", "Studio City", "Woodland Hills", "Porter Ranch", "Pasadena", "Arcadia", "San Marino", "Monrovia", "Manhattan Beach", "Redondo Beach", "Torrance", "El Segundo"]
  },
  "phoenix-area": {
    name: "Phoenix Area",
    cities: ["Phoenix", "Scottsdale", "Tempe", "Mesa", "Chandler", "Gilbert", "Glendale", "Peoria", "Surprise", "Goodyear"]
  }
};

export type LocationType = {
  slug: string;
  name: string;
  type: 'region' | 'city';
  regionSlug: keyof typeof REGIONS;
};

export const ALL_LOCATIONS: LocationType[] = Object.entries(REGIONS).flatMap(([regionSlug, region]) => [
  { slug: regionSlug, name: region.name, type: 'region', regionSlug: regionSlug as keyof typeof REGIONS },
  ...region.cities.map(city => ({
    slug: city.toLowerCase().replace(/\s+/g, '-'),
    name: city,
    type: 'city' as const,
    regionSlug: regionSlug as keyof typeof REGIONS
  }))
]);

export const LocationContext = createContext<LocationType>({
  slug: "sacramento-area",
  name: "Sacramento",
  type: 'city',
  regionSlug: 'sacramento-area'
});

const LocationUpdateContext = createContext<(loc: LocationType) => void>(() => {});

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [location, setLocationState] = useState<LocationType>(() => {
    const saved = localStorage.getItem("lastRegion");
    if (saved && REGIONS[saved as keyof typeof REGIONS]) {
      return {
        slug: saved,
        name: REGIONS[saved as keyof typeof REGIONS].name,
        type: 'region',
        regionSlug: saved as keyof typeof REGIONS
      };
    }
    return {
      slug: "sacramento-area",
      name: "Sacramento",
      type: 'city',
      regionSlug: 'sacramento-area'
    };
  });

  const setLocation = (loc: LocationType) => {
    setLocationState(loc);
    if (loc.type === 'region') {
      localStorage.setItem("lastRegion", loc.regionSlug);
    }
  };

  return (
    <LocationContext.Provider value={location}>
      <LocationUpdateContext.Provider value={setLocation}>
        {children}
      </LocationUpdateContext.Provider>
    </LocationContext.Provider>
  );
};

export const useLocation = () => useContext(LocationContext);
export const useLocationUpdate = () => useContext(LocationUpdateContext);

